import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Typography,
  Paper,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { SimpleTable, type SimpleTableColumn } from '../../../../components/common';
import { DuenioSelect, EncargadoSelect } from '../../../../components/parametros';
import { type ArticuloMateriaPrima } from '../types';

interface ArticulosMateriaPrimaTableProps {
  articulos: ArticuloMateriaPrima[];
  onCantidadChange?: (id: number, cantidad: number) => void;
  onDuenioChange?: (id: number, duenio: number) => void;
  onEncargadoChange?: (id: number, encargado: number) => void;
  onRemoveArticulo?: (id: number) => void;
  onAddArticulo?: () => void;
  onEditPresentacion?: (idPresentacion: number, productos: ArticuloMateriaPrima[]) => void;
  numeroMallas?: number;
  readonly?: boolean;
  showValidation?: boolean;
  showDercSummary?: boolean;
}

const ArticulosMateriaPrimaTable: React.FC<ArticulosMateriaPrimaTableProps> = ({
  articulos,
  onCantidadChange,
  onDuenioChange,
  onEncargadoChange,
  onRemoveArticulo,
  onAddArticulo,
  onEditPresentacion,
  numeroMallas = 0,
  readonly = false,
  showValidation = false,
  showDercSummary = true,
}) => {
  const [expandedPresentacion, setExpandedPresentacion] = useState<string | false>(false);
  const prevArticulosLengthRef = useRef(articulos.length);

  const handleCantidadChange = (id: number, value: string) => {
    if (readonly || !onCantidadChange) return;
    const numericValue = parseFloat(value) || 0;
    onCantidadChange(id, numericValue);
  };

  // Agrupar artículos por presentación
  const articulosPorPresentacion = articulos.reduce((grupos, articulo) => {
    const presentacion = articulo.presentacion || 'Sin Presentación';
    if (!grupos[presentacion]) {
      grupos[presentacion] = [];
    }
    grupos[presentacion].push(articulo);
    return grupos;
  }, {} as Record<string, ArticuloMateriaPrima[]>);

  // Auto-expand accordion when new products are added
  useEffect(() => {
    if (articulos.length > prevArticulosLengthRef.current && articulos.length > 0) {
      // New products were added - expand the accordion of the last added product
      const lastProduct = articulos[articulos.length - 1];
      const presentacion = lastProduct.presentacion || 'Sin Presentación';
      setExpandedPresentacion(presentacion);
    }
    prevArticulosLengthRef.current = articulos.length;
  }, [articulos]);

  const handleAccordionChange = (presentacion: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPresentacion(isExpanded ? presentacion : false);
  };

  // Calcular totales
  const totalCantidad = articulos.reduce((sum, producto) => sum + (producto.cantidad || 0), 0);
  const diferenciaMallas = totalCantidad - numeroMallas;


  const productColumns: SimpleTableColumn[] = [
    {
      id: 'codigoArticulo',
      label: 'Código',
      align: 'left',
      width: 120,
    },
    {
      id: 'descripcion',
      label: 'Descripción',
      align: 'left',
    },
    {
      id: 'duenio',
      label: 'Dueño',
      align: 'left',
      width: 200,
      render: (_value: any, row: ArticuloMateriaPrima) => {
        const hasCantidad = row.cantidad > 0;
        const isEmpty = !row.duenio || row.duenio === '';
        const showError = showValidation && hasCantidad && isEmpty;
        return (
          <Box sx={{ minWidth: 180 }}>
            <DuenioSelect
              value={row.duenio || ''}
              onChange={(val) => onDuenioChange?.(row.id, val)}
              fullWidth
              disabled={readonly}
              required
              error={showError ? 'Requerido' : undefined}
            />
          </Box>
        );
      },
    },
    {
      id: 'encargado',
      label: 'Encargado',
      align: 'left',
      width: 200,
      render: (_value: any, row: ArticuloMateriaPrima) => {
        const hasCantidad = row.cantidad > 0;
        const isEmpty = !row.encargado || row.encargado === '';
        const showError = showValidation && hasCantidad && isEmpty;
        return (
          <Box sx={{ minWidth: 180 }}>
            <EncargadoSelect
              value={row.encargado || ''}
              onChange={(val) => onEncargadoChange?.(row.id, val)}
              fullWidth
              disabled={readonly}
              required
              error={showError ? 'Requerido' : undefined}
            />
          </Box>
        );
      },
    },
    {
      id: 'pesoEmpaqueInterior',
      label: 'Peso Empaque (kg)',
      align: 'right',
      width: 120,
      render: (_value: any, row: ArticuloMateriaPrima) => (
        <Box sx={{ textAlign: 'right' }}>
          {(row.pesoEmpaqueInterior || 0).toFixed(2)}
        </Box>
      ),
    },
    {
      id: 'cantidad',
      label: 'Cantidad',
      align: 'right',
      width: 100,
      render: (_value: any, row: ArticuloMateriaPrima) => (
        readonly ? (
          <Box sx={{ textAlign: 'right' }}>
            {(row.cantidad || 0).toFixed(2)}
          </Box>
        ) : (
          <TextField
            size="small"
            type="number"
            value={row.cantidad === 0 ? '' : row.cantidad}
            onChange={(e) => handleCantidadChange(row.id, e.target.value)}
            onFocus={(e) => e.target.select()}
            placeholder="0"
            slotProps={{
              input: {
                style: { textAlign: 'right', width: '80px' }
              },
              htmlInput: {
                min: 0,
                step: 0.01
              }
            }}
            variant="outlined"
          />
        )
      ),
    },
    {
      id: 'total',
      label: 'Total',
      align: 'right',
      width: 100,
      render: (_value: any, row: ArticuloMateriaPrima) => (
        <Box sx={{ textAlign: 'right', fontWeight: 500 }}>
          {(row.total || 0).toFixed(2)}
        </Box>
      ),
    },
    ...(!readonly ? [{
      id: 'actions',
      label: 'Acciones',
      align: 'center' as const,
      width: 80,
      render: (_value: any, row: ArticuloMateriaPrima) => (
        <Tooltip title="Eliminar producto">
          <IconButton
            size="small"
            onClick={() => onRemoveArticulo?.(row.id)}
            sx={{
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }] : []),
  ];

  return (
    <Box>
      {!readonly && onAddArticulo && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddArticulo}
            size="small"
          >
            Agregar Producto
          </Button>
        </Box>
      )}

      {articulos.length === 0 && (
        <Typography variant="body1" color="text.secondary" align="center" py={4}>
          No hay productos agregados
        </Typography>
      )}

      {/* Acordeones por presentación */}
      {Object.entries(articulosPorPresentacion).map(([presentacion, productosGrupo]) => {
        const totalCantidadGrupo = productosGrupo.reduce((sum, p) => sum + (p.cantidad || 0), 0);
        const totalTotalGrupo = productosGrupo.reduce((sum, p) => sum + (p.total || 0), 0);

        return (
          <Accordion
            key={presentacion}
            expanded={expandedPresentacion === presentacion}
            onChange={handleAccordionChange(presentacion)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: 'grey.50',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {presentacion}
                </Typography>
                <Chip
                  label={`${productosGrupo.length} productos`}
                  size="small"
                  color="primary"
                />
                <Box sx={{ ml: 'auto', display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Chip
                    label={`Cantidad: ${totalCantidadGrupo.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Total: ${totalTotalGrupo.toFixed(2)} kg`}
                    size="small"
                    variant="outlined"
                    color="secondary"
                  />
                  {!readonly && onEditPresentacion && productosGrupo[0]?.idPresentacion && (
                    <Tooltip title="Editar presentación">
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditPresentacion(productosGrupo[0].idPresentacion!, productosGrupo);
                        }}
                        sx={{
                          ml: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          color: 'primary.main',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <SimpleTable
                columns={productColumns}
                rows={productosGrupo}
                emptyMessage="No hay productos en este grupo"
                getRowId={(row) => row.id}
                hover={true}
              />

              {/* Fila de totales del grupo */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'primary.light',
                  borderRadius: 1,
                  p: 1.5,
                  mt: 1,
                  fontWeight: 600,
                }}
              >
                <Box sx={{ width: 120, px: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    SUBTOTALES
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, px: 2 }} />
                <Box sx={{ width: 150, px: 2 }} /> {/* Dueño */}
                <Box sx={{ width: 150, px: 2 }} /> {/* Encargado */}
                <Box sx={{ width: 120, px: 2, textAlign: 'right' }} /> {/* Peso Empaque */}
                <Box sx={{ width: 100, px: 2, textAlign: 'right' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {totalCantidadGrupo.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ width: 100, px: 2, textAlign: 'right' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {totalTotalGrupo.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ width: 80 }} /> {/* Acciones */}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Sumatoria de Producto Terminado */}
      {articulos.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
          <Chip label={`Cajas: ${totalCantidad.toFixed(2)}`} size="small" color="primary" variant="outlined" />
          <Chip label="Bolsas: 0.00" size="small" color="primary" variant="outlined" />
          <Chip label="Saldos: 0.00" size="small" color="primary" variant="outlined" />
          <Chip label="Otros: 0.00" size="small" color="primary" variant="outlined" />
          <Chip label={`Peso: ${articulos.reduce((sum, p) => sum + (p.total || 0), 0).toFixed(2)}`} size="small" color="primary" variant="outlined" />
        </Box>
      )}

      {/* Resumen de mallas DERC */}
      {showDercSummary && articulos.length > 0 && (
        <Paper sx={{ p: 2, mt: 2, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            Resumen DERC
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight={500}>
                  Total Cantidad:
                </Typography>
                <Chip
                  label={totalCantidad.toFixed(0)}
                  color="primary"
                  size="small"
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight={500}>
                  Mallas DERC:
                </Typography>
                <Chip
                  label={numeroMallas.toFixed(0)}
                  color="info"
                  size="small"
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight={500}>
                  Diferencia:
                </Typography>
                <Chip
                  label={`${diferenciaMallas >= 0 ? '+' : ''}${diferenciaMallas.toFixed(0)}`}
                  color={diferenciaMallas === 0 ? 'success' : diferenciaMallas > 0 ? 'warning' : 'error'}
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default ArticulosMateriaPrimaTable;